import {
  VaultInviteOriginFilterIds,
  VaultOwnershipFiltersIds,
  VaultStatusFiltersIds,
  VaultTypeFiltersIds,
  CollaboratorStatusFiltersIds,
  CollaboratorTypeFilterIds,
} from 'common/enums';
import { CollaboratorsQueryParams } from 'models/collaborators-query-params';
import { Pagination } from 'models/pagination';
import { collaboratorsActions } from './action-types';

const vaultAccessFiltersArray: CheckboxOptionSimpleType[] = [
  {
    id: VaultOwnershipFiltersIds.owner,
    name: 'Vaults owned by me',
    isSelected: false,
  },
  {
    id: VaultOwnershipFiltersIds.contributor,
    name: 'Vaults as an editor',
    isSelected: false,
  },
];

const collaboratorRoleFiltersArray: CheckboxOptionSimpleType[] = [
  {
    id: CollaboratorTypeFilterIds.contributors,
    name: 'Editors',
    isSelected: false,
  },
  {
    id: CollaboratorTypeFilterIds.guests,
    name: 'Contributors',
    isSelected: false,
  },
];

const inviteOriginFiltersArray: CheckboxOptionSimpleType[] = [
  {
    id: VaultInviteOriginFilterIds.myself,
    name: 'Invites sent by me',
    isSelected: false,
  },
  {
    id: VaultInviteOriginFilterIds.otherContributor,
    name: 'Invites sent by another editor',
    isSelected: false,
  },
];

const vaultTypeFiltersArray: CheckboxOptionSimpleType[] = [
  {
    id: VaultTypeFiltersIds.legacy,
    name: 'Legacy vault',
    isSelected: false,
  },
  {
    id: VaultTypeFiltersIds.lifetime,
    name: 'Lifetime vault',
    isSelected: false,
  },
  {
    id: VaultTypeFiltersIds.standard,
    name: 'Standard vault',
    isSelected: false,
  },
  {
    id: VaultTypeFiltersIds.readOnly,
    name: 'Read-only vault',
    isSelected: false,
  },
];

const collaboratorStatusFiltersArray: CheckboxOptionSimpleType[] = [
  {
    id: CollaboratorStatusFiltersIds.active,
    name: 'Active',
    isSelected: false,
  },
  {
    id: CollaboratorStatusFiltersIds.pending,
    name: 'Pending',
    isSelected: false,
  },
  {
    id: CollaboratorStatusFiltersIds.removed,
    name: 'Removed',
    isSelected: false,
  },
  {
    id: CollaboratorStatusFiltersIds.expired,
    name: 'Expired',
    isSelected: false,
  },
];

export const vaultStatusFiltersArray: CheckboxOptionSimpleType[] = [
  { id: VaultStatusFiltersIds.active, name: 'Active vault', isSelected: false },
  {
    id: VaultStatusFiltersIds.restricted,
    name: 'Restricted vault',
    isSelected: false,
  },
  { id: VaultStatusFiltersIds.locked, name: 'Locked vault', isSelected: false },
];

const collaboratorActivityFiltersArray: CheckboxOptionSimpleType[] = [
  { id: 'yes', name: 'Has contributed to vault', isSelected: false },
  {
    id: 'no',
    name: 'Has not contributed to vault',
    isSelected: false,
  },
];

const vaultAccessGroupFilter: CheckboxGroupType = {
  id: 'accessLevel',
  name: 'Vault access',
  options: vaultAccessFiltersArray,
};

const collaboratorRoleGroupFilter: CheckboxGroupType = {
  id: 'collaboratorRole',
  name: 'Role',
  options: collaboratorRoleFiltersArray,
};

const inviteOriginGroupFilter: CheckboxGroupType = {
  id: 'inviteOrigin',
  name: 'Invites origin',
  options: inviteOriginFiltersArray,
};

const collaboratorStatusGroupFilter: CheckboxGroupType = {
  id: 'collaboratorStatus',
  name: 'Collaborator status',
  options: collaboratorStatusFiltersArray,
};

const vaultTypeGroupFilter: CheckboxGroupType = {
  id: 'vaultType',
  name: 'Vault type',
  options: vaultTypeFiltersArray,
};

const vaultStatusGroupFilter: CheckboxGroupType = {
  id: 'vaultStatus',
  name: 'Vault status',
  options: vaultStatusFiltersArray,
};

const hasContributedToVaultGroupFilter: CheckboxGroupType = {
  id: 'collaboratorActivity',
  name: 'Collaborator activity',
  options: collaboratorActivityFiltersArray,
};

export type InitialStateType = {
  checkboxFilters: CheckboxGroupType[];
  isLoading: boolean;
  isSearching: boolean;
  searchValue: string;
  vaultsCollaborators: VaultCollaboratorsType[];
  pagination: PaginationType;
  queryParams: CollaboratorsQueryParamsType;
  refetchCollaborators: boolean;
};

const initialState: InitialStateType = {
  checkboxFilters: [
    vaultAccessGroupFilter,
    collaboratorRoleGroupFilter,
    inviteOriginGroupFilter,
    collaboratorStatusGroupFilter,
    vaultTypeGroupFilter,
    vaultStatusGroupFilter,
    hasContributedToVaultGroupFilter,
  ],
  searchValue: '',
  isLoading: false,
  isSearching: false,
  vaultsCollaborators: [],
  pagination: new Pagination(),
  queryParams: new CollaboratorsQueryParams(),
  refetchCollaborators: false,
};

type Action = {
  type: string;
  checkboxFilters: CheckboxGroupType[];
  searchValue: string;
  isLoading: boolean;
  isSearching: boolean;
  queryParams: CollaboratorsQueryParamsType;
  pagination: PaginationType;
  vaultsCollaborators: VaultCollaboratorsType[];
  refetchCollaborators: boolean;
  editCollaboratorInfo: {
    vaultId: number;
    collaborator: VaultCollaboratorType;
  };
};

const CollaboratorsReducer = (state: InitialStateType, action: Action) => {
  switch (action.type) {
    case collaboratorsActions.UPDATE_CHECKBOX_FILTERS:
      return {
        ...state,
        checkboxFilters: action.checkboxFilters,
      };
    case collaboratorsActions.IS_LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case collaboratorsActions.IS_SEARCHING:
      return {
        ...state,
        isSearching: action.isSearching,
      };
    case collaboratorsActions.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: action.pagination,
      };
    case collaboratorsActions.UPDATE_QUERY_PARAMS:
      return {
        ...state,
        queryParams: action.queryParams,
      };
    case collaboratorsActions.UPDATE_VAULTS:
      return {
        ...state,
        vaultsCollaborators: action.vaultsCollaborators,
      };
    case collaboratorsActions.UPDATE_SEARCH_VALUE:
      return {
        ...state,
        searchValue: action.searchValue,
      };
    case collaboratorsActions.SET_REFETCH_COLLABORATORS:
      return {
        ...state,
        refetchCollaborators: action.refetchCollaborators,
      };
    case collaboratorsActions.UPDATE_COLLABORATOR:
      return {
        ...state,
        vaultsCollaborators: state.vaultsCollaborators.map((vault) => {
          const { editCollaboratorInfo } = action;
          if (vault.id === editCollaboratorInfo.vaultId) {
            return {
              ...vault,
              collaborators: vault.collaborators.map((collaborator) => {
                if (
                  editCollaboratorInfo.collaborator.id === collaborator.id &&
                  editCollaboratorInfo.collaborator.email === collaborator.email
                ) {
                  return action.editCollaboratorInfo.collaborator;
                }
                return collaborator;
              }),
            };
          }
          return vault;
        }),
      };
    default:
      return state;
  }
};

export { CollaboratorsReducer, initialState };
